import React, { FC, RefObject, useMemo, useRef } from 'react';
import type { GroupBase, SingleValueProps, StylesConfig } from 'react-select';
import { components } from 'react-select';
import Select from 'react-select/base';

import { FieldArrayRenderProps, FormikProps } from 'formik';

import AudienceFilterChip from '../../../../../components/formulas/chips/AudienceFilterChip';
//components
import SearchableSelect, {
    SearchableSelectType,
} from '../../../../../components/inputs/searchableSelect';
import TextInput from '../../../../../components/inputs/textInput';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

//models
import {
    CartFilterFormValues,
    CMSFilterCreateModel,
    CMSInputOperators,
    CMSInputType,
    CMSOperators,
    CMSPlaceholderModel,
} from '../../../../../models/cms-placeholder.model';

//helpers
import { getCmsAlreadyUsedOperators } from '../helper';

interface StyledProps {
    isValueSelect?: boolean;
}

interface InputStyledProps {
    width?: string;
}

const customStyles: StylesConfig<
    SearchableSelectType,
    boolean,
    GroupBase<SearchableSelectType>
> = {
    control: (provided) => ({
        ...provided,
        boxShadow: 'none',
        border: 'none',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: aampeTheme.spacing(0.5, 0),
        cursor: 'pointer',
    }),
};

const SingleValue = (
    props: SingleValueProps<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    > &
        StyledProps,
) => {
    return (
        <components.SingleValue {...props}>
            <FilterChip
                title={props.data.label}
                isValueSelect={props.isValueSelect}
            />
        </components.SingleValue>
    );
};

interface CMSPlaceholderFilterRowProps {
    index: number;
    placeholderList: CMSPlaceholderModel[];
    formikProps: FormikProps<CartFilterFormValues>;
    inputRowData: CMSFilterCreateModel;
    placeholderRef: RefObject<
        Select<SearchableSelectType, false, GroupBase<SearchableSelectType>>
    >;
    arrayHelpers: FieldArrayRenderProps;
    menuPortalId?: string;
    isDisabled: boolean;
}
const CMSPlaceholderFilterRow: FC<CMSPlaceholderFilterRowProps> = ({
    index,
    placeholderList,
    formikProps,
    inputRowData,
    placeholderRef,
    arrayHelpers,
    menuPortalId,
    isDisabled,
}) => {
    const { values, setFieldValue, setFieldTouched, handleChange, handleBlur } =
        formikProps;

    const operatorRef =
        useRef<
            Select<SearchableSelectType, false, GroupBase<SearchableSelectType>>
        >(null);

    const valueRef =
        useRef<
            Select<SearchableSelectType, false, GroupBase<SearchableSelectType>>
        >(null);

    const onPlaceholderChange = (
        field: string,
        attributeValue: SearchableSelectType | SearchableSelectType[],
    ) => {
        setFieldValue(field, (attributeValue as SearchableSelectType).value);
        setFieldValue(`cms_filters.${index}.operator`, '');
        setFieldValue(`cms_filters.${index}.value`, '');
        setTimeout(() => operatorRef.current?.focus(), 50);
    };

    const onOperatorChange = (
        field: string,
        operator: SearchableSelectType | SearchableSelectType[],
    ) => {
        setFieldValue(field, (operator as SearchableSelectType).value);
        setTimeout(() => valueRef.current?.focus(), 50);
    };

    const onDropdownInputChange = (
        field: string,
        operator: SearchableSelectType | SearchableSelectType[],
    ) => {
        setFieldValue(field, (operator as SearchableSelectType).value);
    };

    const onTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event);
    };

    const filterPlaceholder = useMemo(() => {
        return placeholderList.find(
            (placeholder) =>
                placeholder?.id === inputRowData.cms_placeholder_id,
        );
    }, [placeholderList, inputRowData]);

    const operator = useMemo(
        () =>
            Object.values(CMSOperators).find(
                (op) => op === inputRowData.operator,
            ),
        [inputRowData],
    );

    const valueOptions = useMemo(() => {
        if (filterPlaceholder?.input_type === CMSInputType.Boolean) {
            return [
                { value: 'true', label: 'True' },
                { value: 'false', label: 'False' },
            ];
        } else {
            return filterPlaceholder?.values?.map((filterValue) => ({
                value: filterValue,
                label: filterValue,
            }));
        }
    }, [filterPlaceholder]);

    const selectedValue = useMemo(() => {
        if (
            filterPlaceholder?.input_type === CMSInputType.Boolean &&
            inputRowData.value
        ) {
            if (inputRowData.value === 'true') {
                return { value: 'true', label: 'True' };
            } else {
                return { value: 'false', label: 'False' };
            }
        } else {
            return { value: inputRowData.value, label: inputRowData.value };
        }
    }, [filterPlaceholder, inputRowData]);

    const menuPortal =
        document.getElementById(menuPortalId ?? '') ?? document.body;
    const operatorOptions = useMemo<SearchableSelectType[]>(() => {
        const alreadySelectedOperators = getCmsAlreadyUsedOperators(
            values.cms_filters,
            inputRowData,
        );
        return CMSInputOperators[
            filterPlaceholder?.input_type ?? CMSInputType.String
        ]
            .map((operator) => ({
                value: operator,
                label: operator,
            }))
            .filter(
                (operator) =>
                    !alreadySelectedOperators?.includes(operator?.value) ||
                    operator?.value === CMSOperators.Equals,
            );
    }, [filterPlaceholder, inputRowData, values]);

    return (
        <Row>
            <Element width={'32%'}>
                <Inputs onClick={(e) => e.stopPropagation()}>
                    <SearchableSelect
                        menuPortalTarget={menuPortal}
                        menuPosition="fixed"
                        name={`cms_filters.${index}.cms_placeholder_id`}
                        options={(placeholderList || []).map((placeholder) => ({
                            value: placeholder?.id,
                            label: placeholder?.name,
                        }))}
                        onChange={onPlaceholderChange}
                        value={
                            filterPlaceholder && {
                                value: filterPlaceholder?.id,
                                label: filterPlaceholder?.name,
                            }
                        }
                        onBlur={setFieldTouched}
                        customStyles={customStyles}
                        components={{
                            SingleValue,
                            DropdownIndicator: () => null,
                        }}
                        ref={placeholderRef}
                        isDisabled={isDisabled}
                    />
                </Inputs>
            </Element>
            <Element width={'20%'}>
                <Inputs onClick={(e) => e.stopPropagation()}>
                    {filterPlaceholder && (
                        <SearchableSelect
                            menuPortalTarget={menuPortal}
                            menuPosition="fixed"
                            name={`cms_filters.${index}.operator`}
                            options={operatorOptions}
                            value={
                                operator && {
                                    value: operator,
                                    label: operator,
                                }
                            }
                            onChange={onOperatorChange}
                            onBlur={setFieldTouched}
                            components={{
                                SingleValue,
                                DropdownIndicator: () => null,
                            }}
                            ref={operatorRef}
                            customStyles={customStyles}
                            isDisabled={isDisabled}
                        />
                    )}
                </Inputs>
            </Element>
            <Element width={'48%'}>
                <Inputs onClick={(e) => e.stopPropagation()}>
                    <span>
                        {operator && valueOptions?.length ? (
                            <SearchableSelect
                                menuPortalTarget={menuPortal}
                                menuPosition="fixed"
                                name={`cms_filters.${index}.value`}
                                options={valueOptions}
                                onChange={onDropdownInputChange}
                                value={selectedValue}
                                onBlur={setFieldTouched}
                                components={{
                                    SingleValue: (innerProps) => (
                                        <SingleValue
                                            isValueSelect
                                            {...innerProps}
                                        />
                                    ),
                                    DropdownIndicator: () => null,
                                }}
                                ref={valueRef}
                                customStyles={customStyles}
                                isDisabled={isDisabled}
                            />
                        ) : (
                            operator && (
                                <TextInput
                                    name={`cms_filters.${index}.value`}
                                    value={values.cms_filters?.[index].value}
                                    onChange={onTextInputChange}
                                    onBlur={handleBlur}
                                    type={
                                        filterPlaceholder?.input_type ===
                                        CMSInputType.String
                                            ? 'text'
                                            : 'number'
                                    }
                                    inputStyle={{
                                        width: '200px',
                                    }}
                                    inputRef={valueRef}
                                    removeBorder
                                    isDisabled={isDisabled}
                                />
                            )
                        )}
                    </span>
                </Inputs>
            </Element>

            {!isDisabled && (
                <span onClick={() => arrayHelpers.remove(index)}>
                    <DeleteIcon />
                </span>
            )}
        </Row>
    );
};

const Row = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(4),
    width: '100%',
});

const Inputs = styled.div({
    display: 'flex',
    flexGrow: 1,
    width: '100%',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(0, 2),
});

const DeleteIcon = styled(ICONS.trashX)`
    margin-top: ${aampeTheme.spacing4(2)};
    cursor: pointer;
`;

const Element = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'width', // Exclude 'width' from being forwarded to the DOM
})<InputStyledProps>(({ width }) => ({
    width,
    display: 'flex',
}));

const FilterChip = styled(AudienceFilterChip).withConfig({
    shouldForwardProp: (prop) => prop !== 'isValueSelect', // Exclude 'isValueSelect' from being forwarded to the DOM
})<StyledProps>(({ isValueSelect }) => ({
    backgroundColor: isValueSelect
        ? aampeTheme.colors.bordersWG01
        : aampeTheme.colors.gray50,
}));

export default CMSPlaceholderFilterRow;
